import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { useQuery } from "@apollo/client";
import { useAuthUser } from "../../contexts/AuthUserContext";
import { useLocalization } from "../../contexts/LocalizationContext";
import { useShop } from "../../contexts/ShopContext";
import { usePrimaryStrategy } from "../../hooks/usePrimaryStrategy";
import { GET_SUBSCRIPTION_STATUS } from "../../graphql/queries";
import { summarizeSubscription } from "../../utils/subscription";
import {
  API_BASE,
} from "../../utils/api/api";
import {
  fetchDailyOverviewReport,
  fetchConversionAndPdpAssessmentReport,
  fetchConversionAndPdpWindows,
  fetchHomepageAssessmentReport,
  fetchSocialMediaAssessmentReport,
} from "../../utils/api/reportApi";
import {
  ASSESSMENT_SUMMARY_ITEM_BY_ID,
  SUMMARY_SECTION_LABELS,
} from "../../utils/assessmentSummaryConfig";
import {
  ConversionAndPdpViewer,
  HomepageAssessmentViewer,
  SocialMediaAssessmentViewer,
} from "../reportViewers";
import MetaPerformanceAssessmentViewer from "../reportViewers/MetaPerformanceAssessmentViewer";
import StrategyFullReportModal from "./StrategyFullReportModal";
import {
  CrawlAndIndexing,
  ProductIntelligenceDeepClean,
  ShopSeoRemediation,
  BlogSeoUplift,
  StorewideCompliance,
  RoadmapMilestones,
  FoundationalNarrativeStrategyRenderer,
  isNarrativeStrategyBlock,
} from "../strategyViewer/FoundationalStage";
import {
  ExecutivePulse,
  ShopifySyncSnapshot,
  GoogleActivationBoard,
  MetaActivationBoard,
} from "../strategyViewer/OnboardingStage";
import styles from "./ZeqiStrategyReportSection.module.css";
import {
  GenericFunnelViewer,
  FunnelRevenueRealityAssessmentViewerV2,
  FunnelFixedCostViabilityAssessmentViewerV2,
  FunnelProductSkuEconomicsAssessmentViewerV2,
  FunnelCustomerLifetimeEconomicsAssessmentViewerV2,
  FunnelGrowthEfficiencyAssessmentViewerV2,
  FunnelMarginStabilityAssessmentViewerV2,
  FunnelOperationalReadinessAssessmentViewerV2,
} from "../assessmentViewer/FunnelStage";
import {
  FoundationNarrativeAssessmentLineRenderer,
  isNarrativeAssessmentLine,
} from "../assessmentViewer/FoundationStage";
import {
  FileText,
  Zap,
  HeartPulse,
  TrendingUp,
  TrendingDown,
  ShieldCheck,
  BarChart3,
  RotateCcw,
  Calculator,
  Scale,
  Users,
  Layers,
  Percent,
  Medal,
  Building2,
  Package,
  Route,
  ShieldAlert,
  ClipboardCheck,
  ChevronLeft,
  Search,
  Megaphone,
  Globe,
  CalendarDays,
  Swords,
} from "lucide-react";

const AssessmentReportSkeleton = () => (
  <div className={styles.reportSkeleton} aria-label="Loading report preview" role="status">
    <div className={styles.reportSkeletonCard}>
      <div className={styles.reportSkeletonCardHeader}>
        <div className={styles.reportSkeletonLine} style={{ height: 12, width: 84 }} />
        <div className={styles.reportSkeletonLine} style={{ height: 12, width: 56, marginLeft: "auto" }} />
      </div>
      <div className={styles.reportSkeletonRows}>
        <div className={styles.reportSkeletonLine} style={{ height: 28, width: "68%" }} />
        <div className={styles.reportSkeletonLine} style={{ height: 14, width: "100%" }} />
        <div className={styles.reportSkeletonLine} style={{ height: 14, width: "88%" }} />
      </div>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
      {[1, 2, 3].map((item) => (
        <div key={item} className={styles.reportSkeletonCard}>
          <div className={styles.reportSkeletonLine} style={{ height: 11, width: "58%" }} />
          <div className={styles.reportSkeletonLine} style={{ height: 24, width: "78%" }} />
        </div>
      ))}
    </div>
    <div className={styles.reportSkeletonCard}>
      <div className={styles.reportSkeletonRows}>
        {[100, 86, 94, 72].map((width) => (
          <div key={width} className={styles.reportSkeletonLine} style={{ height: 13, width: `${width}%` }} />
        ))}
      </div>
    </div>
  </div>
);

const asObject = (val) => {
  if (!val) return null;
  if (typeof val === "object") return val;
  if (typeof val === "string") {
    try {
      return JSON.parse(val);
    } catch {
      return { text: val };
    }
  }
  return { text: String(val) };
};

const toArrayFirst = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      const obj = asObject(parsed);
      if (obj && (obj.description || obj.text || obj.summary)) {
        return [obj.description || obj.text || obj.summary];
      }
      return [raw];
    } catch {
      return [raw];
    }
  }
  const obj = asObject(raw);
  if (!obj) return null;
  const picked = obj.description ?? obj.text ?? obj.summary;
  if (picked) return [picked];

  const collected = [];
  Object.entries(obj).forEach(([, value]) => {
    if (!value) return;
    if (typeof value === "string") {
      collected.push(value);
      return;
    }
    if (typeof value === "object") {
      if (typeof value.markdown === "string") collected.push(value.markdown);
      else if (typeof value.text === "string") collected.push(value.text);
      else if (typeof value.description === "string")
        collected.push(value.description);
    }
  });

  return collected.length ? collected : null;
};

const stripMd = (value = "") =>
  value
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    .replace(/`{1,3}([^`]*)`{1,3}/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    .replace(/\r?\n/g, " ")
    .trim();

const stripFirstBoldTitle = (markdown = "") => {
  const text = String(markdown || "");
  return text.replace(/^\s*\*\*[^*]+\*\*\s*/, "").trim();
};

const normalizeStrategyTitle = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[&]/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const STRATEGY_FLOW_ORDER = [
  "crawl and indexing",
  "product intelligence deep clean",
  "shop seo remediation",
  "blog seo uplift",
  "storewide and policy compliance",
  "roadmap and milestones",
];

const getStrategyFlowIndex = (label = "") => {
  const normalized = normalizeStrategyTitle(label);
  const idx = STRATEGY_FLOW_ORDER.findIndex((item) =>
    normalized.includes(item),
  );
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

const ONBOARDING_FLOW_ORDER = [
  "zeqi onboarding overview",
  "connect shopify",
  "connect google",
  "connect meta",
];

const getOnboardingFlowIndex = (label = "") => {
  const normalized = normalizeStrategyTitle(label);
  const idx = ONBOARDING_FLOW_ORDER.findIndex((item) =>
    normalized.includes(item),
  );
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

const renderFoundationalStrategySection = (label = "", markdown = "") => {
  const normalized = normalizeStrategyTitle(label);

  if (normalized.includes("crawl and indexing")) {
    return <CrawlAndIndexing markdown={markdown} />;
  }
  if (normalized.includes("product intelligence deep clean")) {
    return <ProductIntelligenceDeepClean markdown={markdown} />;
  }
  if (normalized.includes("shop seo remediation")) {
    return <ShopSeoRemediation markdown={markdown} />;
  }
  if (normalized.includes("blog seo uplift")) {
    return <BlogSeoUplift markdown={markdown} />;
  }
  if (normalized.includes("storewide and policy compliance")) {
    return <StorewideCompliance markdown={markdown} />;
  }
  if (normalized.includes("roadmap and milestones")) {
    return <RoadmapMilestones markdown={markdown} />;
  }
  if (isNarrativeStrategyBlock(markdown)) {
    return <FoundationalNarrativeStrategyRenderer markdown={markdown} />;
  }

  return (
    <div className={styles.strategyFallback}>
      <h3 className={styles.strategyFallbackTitle}>{label}</h3>
      <p className={styles.strategyFallbackBody}>
        {stripMd(stripFirstBoldTitle(markdown))}
      </p>
    </div>
  );
};

const renderOnboardingStrategySection = (label = "", markdown = "") => {
  const normalized = normalizeStrategyTitle(label);

  if (normalized.includes("zeqi onboarding overview")) {
    return <ExecutivePulse markdown={markdown} />;
  }
  if (normalized.includes("connect shopify")) {
    return <ShopifySyncSnapshot markdown={markdown} />;
  }
  if (normalized.includes("connect google")) {
    return <GoogleActivationBoard markdown={markdown} />;
  }
  if (normalized.includes("connect meta")) {
    return <MetaActivationBoard markdown={markdown} />;
  }

  return (
    <div className={styles.strategyFallback}>
      <h3 className={styles.strategyFallbackTitle}>{label}</h3>
      <p className={styles.strategyFallbackBody}>
        {stripMd(stripFirstBoldTitle(markdown))}
      </p>
    </div>
  );
};

const normalizeAssessmentLabel = (label = "") => {
  const cleaned = String(label || "").trim();
  if (!cleaned) return cleaned;
  const withoutUnit = cleaned.replace(/^unit economics?\s*[-–—:]+\s*/i, "");
  const normalized = withoutUnit.trim();
  if (/^site performance$/i.test(normalized)) {
    return "Core Web Vitals";
  }
  if (/^do we make money per order\??$/i.test(normalized)) {
    return "Do we make money per order?";
  }
  if (/^report$/i.test(normalized)) {
    return "Performance";
  }
  return normalized;
};

const getAssessmentLabel = (markdown = "", index = 0) => {
  const text = String(markdown || "").trim();
  if (!text) return `Assessment ${index + 1}`;

  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const looksLikeTableHeader = (line = "") =>
    /\bmetric\b.*\bvalue\b/i.test(line) && line.includes("|");

  const isTableLine = (line = "") =>
    line.includes("|") || /^[-:|\s]+$/.test(line);

  const isSectionHeading = (line = "") =>
    /^#{1,6}\s+/.test(line) ||
    /^(what does this mean|scoring interpretation|repeatability score)\b/i.test(
      line.trim(),
    );

  const isBadLabel = (label = "") => {
    const normalized = String(label || "")
      .replace(/\s+/g, " ")
      .trim();
    if (!normalized) return true;
    if (looksLikeTableHeader(normalized)) return true;
    if (/^[|:\-\s]+$/.test(normalized)) return true;
    if (/^metric\s*\|\s*value$/i.test(normalized)) return true;
    if (
      /^(what does this mean|scoring interpretation|repeatability score)\b/i.test(
        normalized,
      )
    )
      return true;
    return false;
  };

  const preTableLines = [];
  for (const line of lines) {
    if (looksLikeTableHeader(line) || isTableLine(line)) break;
    preTableLines.push(line);
  }

  for (const line of preTableLines) {
    const boldMatch = line.match(/\*\*(.+?)\*\*/);
    if (boldMatch?.[1]) {
      const candidate = normalizeAssessmentLabel(boldMatch[1].trim());
      if (!isBadLabel(candidate)) return candidate;
    }
  }

  const preTableTitle = preTableLines.find(
    (line) => !isSectionHeading(line) && !isTableLine(line),
  );
  if (preTableTitle) {
    const candidate = normalizeAssessmentLabel(
      stripMd(preTableTitle).slice(0, 60),
    );
    if (!isBadLabel(candidate)) return candidate;
  }

  const boldAnywhere = text.match(/\*\*(.+?)\*\*/);
  if (boldAnywhere?.[1]) {
    const candidate = normalizeAssessmentLabel(boldAnywhere[1].trim());
    if (!isBadLabel(candidate)) return candidate;
  }

  for (const line of lines) {
    const boldMatch =
      line.match(/\*\*(.+?)\*\*/) ||
      line.match(/^\s*(?:[-*+]|[0-9]+[.)])\s+\*\*(.+?)\*\*/);
    if (boldMatch?.[1]) {
      const candidate = normalizeAssessmentLabel(boldMatch[1].trim());
      if (!isBadLabel(candidate)) return candidate;
    }
  }

  for (const line of lines) {
    const headingMatch = line.match(/^#{1,6}\s+(.+)$/);
    if (headingMatch?.[1]) {
      const candidate = normalizeAssessmentLabel(headingMatch[1].trim());
      if (!isBadLabel(candidate)) return candidate;
    }
  }

  for (const line of lines) {
    if (looksLikeTableHeader(line)) continue;
    if (isTableLine(line)) continue;
    const cleaned = stripMd(line).slice(0, 60);
    const candidate = normalizeAssessmentLabel(cleaned);
    if (!isBadLabel(candidate)) return candidate;
  }

  const fallback = lines.find(
    (line) => !looksLikeTableHeader(line) && !isTableLine(line),
  );
  if (fallback) {
    const candidate = normalizeAssessmentLabel(stripMd(fallback).slice(0, 60));
    if (!isBadLabel(candidate)) return candidate;
  }
  return `Assessment ${index + 1}`;
};

// Get the appropriate icon component for a section based on its title
const getSectionIcon = (title = "") => {
  const t = title.toLowerCase();

  if (t.includes("indexing")) return FileText;
  if (t.includes("site performance")) return Zap;
  if (t.includes("core web vitals")) return HeartPulse;
  if (t === "report" || t.includes("gsc")) return TrendingUp;
  if (t.includes("ctr") || t.includes("click")) return TrendingDown;
  if (t.includes("merchant center")) return ShieldCheck;
  if (t.includes("revenue reality")) return BarChart3;
  if (t.includes("returns") || t.includes("refunds")) return RotateCcw;
  if (t.includes("unit economics") || t.includes("make money per order"))
    return Calculator;
  if (t.includes("cac") || t.includes("ltv") || t.includes("customer lifetime"))
    return Scale;
  if (t.includes("customers come back") || t.includes("retention"))
    return Users;
  if (t.includes("scale spend")) return TrendingUp;
  if (t.includes("revenue diversified") || t.includes("diversified"))
    return Layers;
  if (t.includes("margin dependent") || t.includes("discount")) return Percent;
  if (t.includes("repeatability score") || t.includes("rrs")) return Medal;
  if (t.includes("fixed cost viability")) return Building2;
  if (t.includes("product") && t.includes("sku")) return Package;
  if (t.includes("growth") && t.includes("efficiency")) return Route;
  if (t.includes("margin stability") || t.includes("margin risk"))
    return ShieldAlert;
  if (t.includes("operational readiness") || t.includes("data confidence"))
    return ClipboardCheck;

  return BarChart3; // Default icon
};

const getAssessmentPreviewText = (markdown = "") => {
  const preview = stripMd(stripFirstBoldTitle(String(markdown || "")));
  return preview || stripMd(String(markdown || ""));
};

const getSummarySectionLines = (section = null) => {
  if (!section || typeof section !== "object") return [];

  const lines = [];
  const summary = String(section.summary || "").trim();
  const assessment = String(section.assessment || "").trim();
  const description = String(section.description || "").trim();
  const overallDirection = String(
    section?.creative_guidelines?.overall_direction || "",
  ).trim();

  if (summary) lines.push(summary);
  if (assessment && assessment !== summary) lines.push(assessment);
  if (description && description !== summary && description !== assessment) {
    lines.push(description);
  }
  if (
    overallDirection &&
    !lines.some((line) => line.toLowerCase() === overallDirection.toLowerCase())
  ) {
    lines.push(overallDirection);
  }

  if (Array.isArray(section.assessment_report)) {
    section.assessment_report
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .forEach((item) => lines.push(item));
  }

  return lines;
};

const FOCUSED_SUMMARY_SECTION_META = {
  benchmark: {
    subtitle: "Operational and conversion benchmark interpretation",
    Icon: BarChart3,
  },
  meta: {
    subtitle: "Paid social presence and engagement signal interpretation",
    Icon: Megaphone,
  },
  gsc: {
    subtitle: "Indexing, visibility, and search engagement interpretation",
    Icon: Search,
  },
  merchant_center: {
    subtitle: "Trust readiness, policy risk, and buyer confidence",
    Icon: ShieldCheck,
  },
  market: {
    subtitle: "Market demand, positioning, and category context",
    Icon: Globe,
  },
  target_audience: {
    subtitle: "Audience segmentation, motivations, and messaging fit",
    Icon: Users,
  },
  competitor: {
    subtitle: "Competitive pressure, visibility gap, and positioning risk",
    Icon: Swords,
  },
  yearly_content: {
    subtitle: "Seasonal planning and event sequencing interpretation",
    Icon: CalendarDays,
  },
};

const getSummarySectionOverviewLines = (section = null) => {
  if (!section || typeof section !== "object") return [];

  const lines = [];
  const summary = String(section.summary || "").trim();
  const assessment = String(section.assessment || "").trim();
  const description = String(section.description || "").trim();
  const overallDirection = String(
    section?.creative_guidelines?.overall_direction || "",
  ).trim();

  if (summary) lines.push(summary);
  if (assessment && assessment !== summary) lines.push(assessment);
  if (description && description !== summary && description !== assessment) {
    lines.push(description);
  }
  if (
    overallDirection &&
    !lines.some((line) => line.toLowerCase() === overallDirection.toLowerCase())
  ) {
    lines.push(overallDirection);
  }

  return lines;
};

const FREE_FOUNDATION_ASSESSMENT_PATTERNS = [
  /benchmark/i,
  /shop summary/i,
  /indexing/i,
  /core web vitals/i,
  /site performance/i,
  /^performance$/i,
  /meta report/i,
  /^meta$/i,
  /merchant center/i,
  /google search console/i,
  /shopify sync/i,
  /google stack/i,
  /meta stack/i,
];

const FREE_FUNNEL_ASSESSMENT_PATTERNS = [
  /revenue reality/i,
  /fixed cost viability/i,
  /product.*sku.*economics/i,
];

const LOCKED_ASSESSMENT_TEASERS = [
  {
    pattern: /customer lifetime economics/i,
    teaser:
      "See how much long-term customer value your current acquisition strategy is really creating.",
  },
  {
    pattern: /growth efficiency|channel mix/i,
    teaser:
      "See which channels are creating efficient growth and where your spend is leaking margin.",
  },
  {
    pattern: /margin stability|margin risk/i,
    teaser:
      "See how resilient your margins are under discount pressure, cost shifts, and sales volatility.",
  },
  {
    pattern: /operational readiness|data confidence/i,
    teaser:
      "See whether your systems, reporting, and operating rhythm are strong enough to support scale.",
  },
  {
    pattern: /meta/i,
    teaser:
      "See what your Meta performance is signaling about engagement, creative strength, and conversion opportunities.",
  },
];

const isFreeAccessibleAssessment = ({
  label = "",
  isFunnel = false,
}) => {
  const patterns = isFunnel
    ? FREE_FUNNEL_ASSESSMENT_PATTERNS
    : FREE_FOUNDATION_ASSESSMENT_PATTERNS;
  return patterns.some((pattern) => pattern.test(label));
};

const getLockedAssessmentTitle = (label = "") => {
  const raw = String(label || "").trim();
  if (!raw) return "Unlock Assessment";

  const cleaned = raw
    .replace(/\s+/g, " ")
    .replace(/\bassessment\b\s*:\s*/i, ": ")
    .split(":")[0]
    .trim();

  return `Unlock ${cleaned || "Assessment"}`;
};

const getLockedAssessmentTeaser = (label = "") =>
  LOCKED_ASSESSMENT_TEASERS.find(({ pattern }) => pattern.test(label))?.teaser ||
  `Review the full ${String(label || "assessment")
    .replace(/\s+/g, " ")
    .replace(/\bassessment\b\s*:\s*/i, " assessment: ")
    .trim()} with the Monthly Subscription plan.`;

const renderInlineFunnelAssessmentViewer = ({ label = "", markdown = "" }) => {
  const t = String(label || "").toLowerCase();
  const body = String(markdown || "");
  const hasAssessmentTitle = /\*\*[^*\n]+Assessment\*\*/i.test(body);

  if (!hasAssessmentTitle) {
    return <GenericFunnelViewer markdown={body} title={label || "Funnel Insight"} />;
  }

  if (t.includes("revenue reality")) {
    return <FunnelRevenueRealityAssessmentViewerV2 markdown={body} />;
  }
  if (t.includes("fixed cost viability")) {
    return <FunnelFixedCostViabilityAssessmentViewerV2 markdown={body} />;
  }
  if (t.includes("product") && t.includes("sku") && t.includes("economics")) {
    return <FunnelProductSkuEconomicsAssessmentViewerV2 markdown={body} />;
  }
  if (t.includes("customer") && t.includes("lifetime") && t.includes("economics")) {
    return <FunnelCustomerLifetimeEconomicsAssessmentViewerV2 markdown={body} />;
  }
  if (
    (t.includes("growth") && t.includes("efficiency")) ||
    (t.includes("channel") && t.includes("mix"))
  ) {
    return <FunnelGrowthEfficiencyAssessmentViewerV2 markdown={body} />;
  }
  if (
    (t.includes("margin") && t.includes("stability")) ||
    (t.includes("margin") && t.includes("risk"))
  ) {
    return <FunnelMarginStabilityAssessmentViewerV2 markdown={body} />;
  }
  if (
    (t.includes("operational") && t.includes("readiness")) ||
    (t.includes("data") && t.includes("confidence"))
  ) {
    return <FunnelOperationalReadinessAssessmentViewerV2 markdown={body} />;
  }

  return <GenericFunnelViewer markdown={body} title={label || "Funnel Insight"} />;
};

const META_APP_ID = import.meta.env.VITE_META_APP_ID;
const META_REDIRECT_URI = `${API_BASE}/facebook/callback`;
const META_SCOPES = [
  "email",
  "read_insights",
  "business_management",
  "public_profile",
  "ads_read",
  "ads_management",
  "pages_show_list",
  "pages_read_engagement",
  "pages_manage_posts",
  "pages_manage_metadata",
  "instagram_basic",
  "instagram_content_publish",
].join(",");

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GOOGLE_REDIRECT_URI = `${API_BASE}/google/callback`;
const GOOGLE_SCOPES = [
  "openid",
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile",
  "https://www.googleapis.com/auth/analytics.readonly",
  "https://www.googleapis.com/auth/webmasters.readonly",
  "https://www.googleapis.com/auth/webmasters",
  "https://www.googleapis.com/auth/adsense.readonly",
  "https://www.googleapis.com/auth/content",
  "https://www.googleapis.com/auth/siteverification",
].join(" ");

const ZeqiStrategyReportSection = ({
  initialOpenFullReport = false,
  onInitialReportHandled = () => {},
  assessmentSwitchTargetId,
  view = "assessment",
  isGoogleConnected = false,
  isFacebookConnected = false,
  googleSyncReady = false,
  facebookSyncReady = false,
  tokensLoading = false,
  shopDataReadiness = null,
  shopDataReadinessLoading = false,
  refetchTokens,
  onOpenProductCosts,
  onOpenOperatingCosts,
  isPromoted = false,
  showOnboardingAssessmentConnectCard = false,
  summaryFocusId = "",
  onWindowChange,
}) => {
  const { user } = useAuthUser();
  const { selectedShop, shopData } = useShop();
  const { t, locale } = useLocalization();
  const navigate = useNavigate();
  const isArabic = locale === "ar";
  const authToken = user?.token;
  const resolvedUserId = user?._id || user?.id || user?.userId || null;
  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
  const localGoogleConnected = Boolean(isGoogleConnected);
  const localFacebookConnected = Boolean(isFacebookConnected);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return () => {};
    const mq = window.matchMedia("(min-width: 900px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    if (typeof mq.addEventListener === "function") {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  const { data, loading, error, refetch } = usePrimaryStrategy({
    skip: !authToken,
  });
  const { data: subscriptionData } = useQuery(GET_SUBSCRIPTION_STATUS, {
    skip: !authToken,
    fetchPolicy: "cache-and-network",
    context: { headers },
  });
  const subscriptionSummary = useMemo(
    () => summarizeSubscription(subscriptionData?.getSubscriptionStatus),
    [subscriptionData],
  );
  const hasUnlockedAssessmentAccess = Boolean(
    subscriptionSummary.reportAccess?.canUseAllReports,
  );
  const isFreeTierAssessmentView = !hasUnlockedAssessmentAccess;

  const primary = data?.getMyShopPrimaryStrategy?.primary_strategy || null;
  const secondary = data?.getMyShopPrimaryStrategy?.secondary_strategy || null;
  const primaryCompleted = primary?.isCompleted === true;
  const activeShop = selectedShop || shopData || null;
  const isMongoId = useCallback(
    (value) => /^[a-f0-9]{24}$/i.test(String(value || "")),
    [],
  );
  const resolvedAssessmentShopId = useMemo(() => {
    if (isMongoId(activeShop?._id)) return String(activeShop._id);
    if (isMongoId(activeShop?.id)) return String(activeShop.id);
    if (isMongoId(primary?.shop_id)) return String(primary.shop_id);
    if (isMongoId(primary?.shopId)) return String(primary.shopId);
    return null;
  }, [activeShop?._id, activeShop?.id, isMongoId, primary?.shopId, primary?.shop_id]);

  const normalizeCluster = useCallback((value) => {
    return String(value || "")
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  }, []);

  const primaryCluster = useMemo(
    () =>
      normalizeCluster(primary?.cluster_flag || primary?.recommended_strategy),
    [normalizeCluster, primary?.cluster_flag, primary?.recommended_strategy],
  );
  const secondaryCluster = useMemo(
    () =>
      normalizeCluster(
        secondary?.cluster_flag || secondary?.recommended_strategy,
      ),
    [
      normalizeCluster,
      secondary?.cluster_flag,
      secondary?.recommended_strategy,
    ],
  );
  const showAssessmentSwitch =
    primaryCluster === "foundation stage" &&
    secondaryCluster === "funnel stage";

  const [activeAssessmentTab, setActiveAssessmentTab] = useState(() => {
    const primaryCluster = normalizeCluster(
      primary?.cluster_flag || primary?.recommended_strategy,
    );
    const secondaryCluster = normalizeCluster(
      secondary?.cluster_flag || secondary?.recommended_strategy,
    );
    const showAssessmentSwitch =
      primaryCluster === "foundation stage" &&
      secondaryCluster === "funnel stage";
    return showAssessmentSwitch ? "foundation" : "foundation";
  });
  const [activeStrategyTab, setActiveStrategyTab] = useState(() => {
    const primaryCluster = normalizeCluster(
      primary?.cluster_flag || primary?.recommended_strategy,
    );
    const secondaryCluster = normalizeCluster(
      secondary?.cluster_flag || secondary?.recommended_strategy,
    );
    const showStrategySwitch =
      primaryCluster === "foundation stage" &&
      secondaryCluster === "funnel stage";
    return showStrategySwitch ? "foundation" : "foundation";
  });
  const [fullReportModal, setFullReportModal] = useState(null);
  const [inlineAssessmentReport, setInlineAssessmentReport] = useState(null);
  const [summaryReportData, setSummaryReportData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState("");
  const homepageSummaryWindowsLoadKeyRef = useRef("");
  const conversionSummaryWindowsLoadKeyRef = useRef("");
  const socialSummaryWindowsLoadKeyRef = useRef("");
  const summaryFocus = useMemo(
    () => ASSESSMENT_SUMMARY_ITEM_BY_ID[summaryFocusId] || null,
    [summaryFocusId],
  );
  const hasOpenedInitialReport = useRef(false);

  const showStrategySwitch =
    primaryCluster === "foundation stage" &&
    secondaryCluster === "funnel stage";
  const productCostReady = shopDataReadiness?.missing?.product_cost === false;
  const operatingCostReady = shopDataReadiness?.missing?.operating_cost === false;
  const waitingOnGoogleSync = localGoogleConnected && !googleSyncReady;
  const waitingOnFacebookSync = localFacebookConnected && !facebookSyncReady;
  const waitingOnShopData = !(productCostReady && operatingCostReady);
  const needsConnections =
    !localGoogleConnected ||
    !localFacebookConnected ||
    waitingOnGoogleSync ||
    waitingOnFacebookSync ||
    waitingOnShopData;
  const isOnboardingAssessment =
    view === "assessment" &&
    showOnboardingAssessmentConnectCard &&
    !isPromoted &&
    needsConnections;

  const safeRefetchTokens = useCallback(() => {
    if (typeof refetchTokens === "function") return refetchTokens();
    return Promise.resolve();
  }, [refetchTokens]);

  const startFacebookOAuth = useCallback(() => {
    if (!resolvedUserId) return;
    const state = encodeURIComponent(resolvedUserId);
    const authUrl =
      `https://www.facebook.com/v23.0/dialog/oauth?client_id=${META_APP_ID}` +
      `&redirect_uri=${encodeURIComponent(META_REDIRECT_URI)}` +
      `&response_type=code&scope=${encodeURIComponent(META_SCOPES)}` +
      `&state=${state}`;
    window.open(authUrl, "_blank", "noopener");
    setTimeout(() => safeRefetchTokens().catch(() => {}), 3000);
  }, [resolvedUserId, safeRefetchTokens]);

  const startGoogleOAuth = useCallback(() => {
    if (!resolvedUserId) return;
    const state = encodeURIComponent(resolvedUserId);
    const authUrl =
      `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent(GOOGLE_REDIRECT_URI)}` +
      `&response_type=code&scope=${encodeURIComponent(GOOGLE_SCOPES)}` +
      `&access_type=offline&prompt=consent&state=${state}`;
    window.open(authUrl, "_blank", "noopener");
    setTimeout(() => safeRefetchTokens().catch(() => {}), 3000);
  }, [resolvedUserId, safeRefetchTokens]);

  const getAssessmentPayload = useCallback(
    (strategy) => {
      const assessmentArr = toArrayFirst(strategy?.assessment_report);
      const fullAssessmentText =
        strategy?.problem || (assessmentArr && assessmentArr[0]) || "";
      const assessmentDesc =
        stripMd(fullAssessmentText) ||
        t("StrategyReportSection.assessmentPreviewFallback");

      return {
        assessmentArr,
        fullAssessmentText,
        assessmentDesc,
      };
    },
    [t],
  );

  const activeAssessmentStrategy =
    showAssessmentSwitch && activeAssessmentTab === "performance"
      ? secondary
      : primary;
  const activeAssessmentClusterNormalized = normalizeCluster(
    activeAssessmentStrategy?.cluster_flag ||
      activeAssessmentStrategy?.recommended_strategy,
  );
  const isActiveAssessmentFunnel =
    activeAssessmentClusterNormalized === "funnel stage";

  const activeAssessmentData = useMemo(
    () => getAssessmentPayload(activeAssessmentStrategy),
    [getAssessmentPayload, activeAssessmentStrategy],
  );
  const assessmentItems = useMemo(
    () =>
      Array.isArray(activeAssessmentData.assessmentArr)
        ? activeAssessmentData.assessmentArr.filter(Boolean)
        : [],
    [activeAssessmentData.assessmentArr],
  );
  const getStrategyPayload = useCallback(
    (strategy) => {
      const strategyArr = toArrayFirst(strategy?.description);
      const fullStrategyText =
        strategy?.short_description || (strategyArr && strategyArr[0]) || "";
      const strategyDesc =
        stripMd(fullStrategyText) ||
        t("StrategyReportSection.strategyPreviewFallback");

      return {
        strategyArr,
        fullStrategyText,
        strategyDesc,
      };
    },
    [t],
  );

  const activeStrategyStrategy =
    showStrategySwitch && activeStrategyTab === "performance"
      ? secondary
      : primary;
  const activeStrategyClusterNormalized = normalizeCluster(
    activeStrategyStrategy?.cluster_flag ||
      activeStrategyStrategy?.recommended_strategy,
  );
  const isActiveStrategyFoundational =
    activeStrategyClusterNormalized === "foundation stage";
  const isActiveStrategyOnboarding =
    activeStrategyClusterNormalized === "onboarding stage";

  const activeStrategyData = useMemo(
    () => getStrategyPayload(activeStrategyStrategy),
    [getStrategyPayload, activeStrategyStrategy],
  );

  const reports = useMemo(() => {
    if (!primary) return [];
    return [
      {
        key: "assessment",
        title: t("StrategyReportSection.assessmentTitle"),
        description: activeAssessmentData.assessmentDesc,
        full: {
          title: t("StrategyReportSection.assessmentTitle"),
          description: activeAssessmentData.fullAssessmentText,
          payload: Array.isArray(activeAssessmentData.assessmentArr)
            ? activeAssessmentData.assessmentArr
            : [],
          meta: {
            cluster_flag: activeAssessmentStrategy?.cluster_flag,
            recommended_strategy:
              activeAssessmentStrategy?.recommended_strategy,
          },
        },
      },
      {
        key: "strategy",
        title: t("StrategyReportSection.strategyTitle"),
        description: activeStrategyData.strategyDesc,
        full: {
          title:
            activeStrategyStrategy?.title ||
            t("StrategyReportSection.strategyTitle"),
          description: activeStrategyData.fullStrategyText,
          payload: Array.isArray(activeStrategyData.strategyArr)
            ? activeStrategyData.strategyArr
            : [],
          meta: {
            cluster_flag: activeStrategyStrategy?.cluster_flag,
            recommended_strategy: activeStrategyStrategy?.recommended_strategy,
            research: activeStrategyStrategy?.research,
            updatedAt: activeStrategyStrategy?.updatedAt,
          },
        },
      },
    ];
  }, [
    primary,
    activeAssessmentData,
    activeAssessmentStrategy?.cluster_flag,
    activeAssessmentStrategy?.recommended_strategy,
    activeStrategyData,
    activeStrategyStrategy,
    t,
    locale,
  ]);

  const closeFullReport = useCallback(() => {
    setFullReportModal(null);
  }, []);

  useEffect(() => {
    if (
      !primaryCompleted &&
      primary &&
      initialOpenFullReport &&
      !hasOpenedInitialReport.current
    ) {
      hasOpenedInitialReport.current = true;
      const report = reports.find((r) => r.key === "assessment");
      if (report) {
        const payload = Array.isArray(report?.full?.payload)
          ? report.full.payload.filter(Boolean)
          : [];
        const content =
          payload.length > 0
            ? payload
            : report?.full?.description || report?.description || "";

        const meta =
          activeAssessmentStrategy || report?.full?.meta || primary || null;
        const modalData = {
          title: report?.full?.title || report?.title || "",
          subtitle:
            meta?.recommended_strategy ||
            t("StrategyReportSection.modalSubtitleFallback"),
          clusterFlag: meta?.cluster_flag || "",
          content,
          initialSectionIndex: 0,
        };
        // Use setTimeout to avoid calling setState synchronously within the effect
        setTimeout(() => {
          setFullReportModal(modalData);
        }, 0);
      }
      try {
        onInitialReportHandled?.();
      } catch {
        // ignore
      }
    }
  }, [
    primaryCompleted,
    primary,
    initialOpenFullReport,
    onInitialReportHandled,
    reports,
    activeAssessmentStrategy,
    t,
  ]);

  const assessmentReport = reports.find(
    (report) => report.key === "assessment",
  );
  const isSummaryAssessmentTab =
    showAssessmentSwitch && activeAssessmentTab === "summary";
  const isFocusedSummaryView =
    view === "assessment" && Boolean(summaryFocus);
  const isFocusedBusinessRealityView =
    isFocusedSummaryView && summaryFocusId === "revenue";
  const isFocusedConversionView =
    isFocusedSummaryView && summaryFocusId === "conversion";
  const isFocusedHomepageView =
    isFocusedSummaryView && summaryFocusId === "homepage";
  const isFocusedSocialView =
    isFocusedSummaryView && summaryFocusId === "social";
  const shouldRenderSummaryAssessment =
    (isFocusedSummaryView && !isFocusedBusinessRealityView) ||
    isSummaryAssessmentTab;
  const isSummaryLockedForFree =
    shouldRenderSummaryAssessment && isFreeTierAssessmentView;
  const summaryReportBody = String(summaryReportData?.report_body || "").trim();
  const summarySynthesis = summaryReportData?.synthesis || null;
  const summarySections = summaryReportData?.sections || null;
  const summaryMarkdown = useMemo(() => {
    if (summaryReportBody) {
      return summaryReportBody
        .replace(/^##\s*Executive Summary\s*\n+/i, "")
        .trim();
    }
    if (!summarySynthesis) return "";
    return [
      summarySynthesis.headline || "",
      "",
      "### Market, Revenue, and Audience",
      summarySynthesis.market_position || "",
      summarySynthesis.revenue_reality_summary || "",
      summarySynthesis.growth_opportunity || "",
      summarySynthesis.audience_insight || "",
    ]
      .filter(Boolean)
      .join("\n");
  }, [summaryReportBody, summarySynthesis]);
  const focusedSummarySections = useMemo(() => {
    if (!summaryFocus || !summarySections || typeof summarySections !== "object") {
      return [];
    }
    return summaryFocus.sectionKeys
      .map((key) => {
        const payload = summarySections[key];
        const lines = getSummarySectionLines(payload);
        if (!payload || !lines.length) return null;
        const overviewLines = getSummarySectionOverviewLines(payload);
        const assessmentReport = Array.isArray(payload?.assessment_report)
          ? payload.assessment_report
              .map((item) => String(item || "").trim())
              .filter(Boolean)
          : [];
        return {
          key,
          title: SUMMARY_SECTION_LABELS[key] || key,
          body: lines.join("\n\n"),
          overviewBody: overviewLines.join("\n\n"),
          assessmentReportBody: assessmentReport.join("\n\n"),
          assessmentReportItems: assessmentReport,
        };
      })
      .filter(Boolean);
  }, [summaryFocus, summarySections]);

  const loadSummaryReport = useCallback(async () => {
    if (!resolvedUserId || !authToken) return;
    setSummaryLoading(true);
    setSummaryError("");
    if (isFocusedConversionView || isFocusedHomepageView || isFocusedSocialView) {
      setSummaryReportData(null);
    }
    try {
      const shopId = resolvedAssessmentShopId;
      console.log("[assessment][conversion] loadSummaryReport.start", {
        summaryFocusId,
        isFocusedConversionView,
        isFocusedHomepageView,
        isFocusedSocialView,
        userId: resolvedUserId,
        shopId,
      });
      let resp = null;
      if (isFocusedHomepageView) {
        resp = await fetchHomepageAssessmentReport({
          userId: resolvedUserId,
          shopId,
          authToken,
        });
        setSummaryReportData(resp?.report || null);
        setSummaryLoading(false);
      } else if (isFocusedConversionView) {
        const windowsResponse = await fetchConversionAndPdpWindows({
          userId: resolvedUserId,
          shopId,
          authToken,
        }).catch(() => null);
        const availableWindows = Array.isArray(windowsResponse?.windows)
          ? windowsResponse.windows
          : [];
        const initialWindow =
          availableWindows.find((windowEntry) => windowEntry?.key === "last_7_days") ||
          availableWindows[0] ||
          null;
        resp = await fetchConversionAndPdpAssessmentReport({
            userId: resolvedUserId,
            shopId,
            authToken,
            windowKey: initialWindow?.key || "last_7_days",
            startDate: initialWindow?.startDate || null,
            endDate: initialWindow?.endDate || null,
          });
        if (availableWindows.length && resp?.report?.data && !resp.report.data.available_windows?.length) {
          resp = {
            ...resp,
            report: {
              ...resp.report,
              data: {
                ...resp.report.data,
                available_windows: availableWindows,
              },
            },
          };
        }
      } else if (isFocusedSocialView) {
        resp = await fetchSocialMediaAssessmentReport({
              userId: resolvedUserId,
              shopId,
              authToken,
            });
      } else {
        resp = await fetchDailyOverviewReport({
            userId: resolvedUserId,
            shopId,
            authToken,
          });
      }
      console.log("[assessment][conversion] loadSummaryReport.complete", {
        summaryFocusId,
        isFocusedConversionView,
        isFocusedHomepageView,
        isFocusedSocialView,
        userId: resolvedUserId,
        shopId,
        reportId: resp?.report?._id || resp?.report?.id || null,
        collectionName: resp?.report?.data?.collection_name || null,
        generationKey: resp?.report?.data?.generation_key || null,
        reportingPeriod: resp?.report?.data?.reporting_period || null,
      });
      if (!isFocusedHomepageView) {
        setSummaryReportData(resp?.report || null);
      }
    } catch (err) {
      console.log("[assessment][conversion] loadSummaryReport.error", {
        summaryFocusId,
        isFocusedConversionView,
        isFocusedHomepageView,
        isFocusedSocialView,
        userId: resolvedUserId,
        shopId: resolvedAssessmentShopId,
        error: err?.message || String(err),
      });
      const raw = String(err?.message || "");
      const isNotFound =
        raw.includes("(404)") ||
        raw.toLowerCase().includes("not found");

        if (isNotFound) {
        setSummaryError(
          isFocusedConversionView
            ? "Generating your conversion report now. We’ll notify you when it’s ready."
            : isFocusedHomepageView
              ? "Generating your homepage assessment now. We’ll notify you when it’s ready."
              : isFocusedSocialView
                ? "Generating your social media report now. We’ll notify you when it’s ready."
            : "Generating your summary now. We’ll notify you when it’s ready.",
        );

        // Disabled by product decision:
        // Frontend should not trigger summary creation directly.
        // if (!summaryGenerationTriggered) {
        //   setSummaryGenerationTriggered(true);
        //   try {
        //     await createFoundationalBusinessRealitySummary({
        //       userId: resolvedUserId,
        //       shopId: primary?.shop_id || primary?.shopId || null,
        //       authToken,
        //     });
        //   } catch {
        //     // keep user-facing message clean; creation errors are intentionally silent here
        //   }
        // }
      } else {
        setSummaryError(
          isFocusedConversionView
            ? "Unable to load conversion report right now. Please try again."
            : isFocusedHomepageView
              ? "Unable to load homepage assessment right now. Please try again."
              : isFocusedSocialView
                ? "Unable to load social media report right now. Please try again."
            : "Unable to load summary right now. Please try again."
        );
      }
    } finally {
      setSummaryLoading(false);
    }
  }, [
    resolvedUserId,
    authToken,
    resolvedAssessmentShopId,
    isFocusedConversionView,
    isFocusedHomepageView,
    isFocusedSocialView,
  ]);

  useEffect(() => {
    if (!isFocusedSummaryView) return;
    setActiveAssessmentTab(
      isFocusedBusinessRealityView ? "performance" : "summary",
    );
    setInlineAssessmentReport(null);
    if (!isFreeTierAssessmentView && !isFocusedBusinessRealityView) {
      loadSummaryReport();
    }
  }, [
    isFocusedSummaryView,
    isFocusedBusinessRealityView,
    isFreeTierAssessmentView,
    loadSummaryReport,
  ]);

  const homepageSummaryCollectionName = summaryReportData?.data?.collection_name || "";
  const homepageSummaryGenerationKey =
    summaryReportData?.data?.generation_key ||
    summaryReportData?._id ||
    summaryReportData?.id ||
    "";
  const homepageSummaryAvailableWindowsKey = Array.isArray(summaryReportData?.data?.available_windows)
    ? summaryReportData.data.available_windows.map((windowEntry) => windowEntry?.key).join(",")
    : "";
  const homepageSummaryShopId = resolvedAssessmentShopId || summaryReportData?.shop_id || null;

  useEffect(() => {
    if (!isFocusedSummaryView || summaryFocusId !== "homepage") return undefined;
    if (homepageSummaryCollectionName !== "homepage_assessment") return undefined;
    if (!resolvedUserId || !authToken) return undefined;

    const shopId = homepageSummaryShopId;
    if (!shopId) return undefined;

    const availableWindows = Array.isArray(summaryReportData?.data?.available_windows)
      ? summaryReportData.data.available_windows
      : [];
    const loadedWindows = Array.isArray(summaryReportData?.data?.windows)
      ? summaryReportData.data.windows
      : [];
    if (!availableWindows.length) return undefined;

    const loadedKeys = new Set(loadedWindows.map((windowEntry) => windowEntry?.key).filter(Boolean));
    const generationKey = homepageSummaryGenerationKey;
    const loadKey = `${resolvedUserId}:${shopId}:${generationKey}:${availableWindows.map((windowEntry) => windowEntry?.key).join(",")}`;

    if (homepageSummaryWindowsLoadKeyRef.current === loadKey) return undefined;

    const remainingWindows = availableWindows
      .filter((windowEntry) => windowEntry?.key && !loadedKeys.has(windowEntry.key))
      .sort((left, right) => {
        const leftDays = Number(left?.days);
        const rightDays = Number(right?.days);
        if (Number.isFinite(leftDays) && Number.isFinite(rightDays)) {
          return leftDays - rightDays;
        }
        return String(left?.key || "").localeCompare(String(right?.key || ""));
      });

    if (!remainingWindows.length) {
      homepageSummaryWindowsLoadKeyRef.current = loadKey;
      return undefined;
    }

    let active = true;
    homepageSummaryWindowsLoadKeyRef.current = loadKey;

    const hydrateWindows = async () => {
      for (const windowEntry of remainingWindows) {
        if (!active) break;
        try {
          const resp = await fetchHomepageAssessmentReport({
            userId: resolvedUserId,
            shopId,
            authToken,
            query: {
              section: "assessment",
              windowKey: windowEntry.key,
              startDate: windowEntry.startDate || null,
              endDate: windowEntry.endDate || null,
            },
          });
          if (!active) break;

          const newWindows = Array.isArray(resp?.report?.data?.windows)
            ? resp.report.data.windows
            : [];
          if (!newWindows.length) continue;

          setSummaryReportData((current) => {
            if (!current?.data) return current;
            const existing = Array.isArray(current.data.windows) ? current.data.windows : [];
            const existingKeys = new Set(existing.map((entry) => entry?.key).filter(Boolean));
            const additions = newWindows.filter((entry) => entry?.key && !existingKeys.has(entry.key));
            if (!additions.length) return current;
            return {
              ...current,
              data: {
                ...current.data,
                available_windows: current.data.available_windows?.length
                  ? current.data.available_windows
                  : resp.report.data.available_windows || [],
                windows: [...existing, ...additions],
              },
            };
          });
        } catch (err) {
          console.warn("[assessment][homepage] hydrateWindow.error", {
            windowKey: windowEntry?.key || null,
            error: err?.message || String(err),
          });
        }
      }
    };

    const hydrateTimer = window.setTimeout(hydrateWindows, 500);

    return () => {
      active = false;
      window.clearTimeout(hydrateTimer);
    };
  }, [
    isFocusedSummaryView,
    summaryFocusId,
    homepageSummaryCollectionName,
    homepageSummaryGenerationKey,
    homepageSummaryAvailableWindowsKey,
    homepageSummaryShopId,
    resolvedUserId,
    authToken,
  ]);

  const socialSummaryCollectionName = summaryReportData?.data?.collection_name || "";
  const socialSummaryGenerationKey =
    summaryReportData?.data?.generation_key ||
    summaryReportData?._id ||
    summaryReportData?.id ||
    "";
  const socialSummaryAvailableWindowsKey = Array.isArray(summaryReportData?.data?.available_windows)
    ? summaryReportData.data.available_windows.map((windowEntry) => windowEntry?.key).join(",")
    : "";

  useEffect(() => {
    if (!isFocusedSummaryView || summaryFocusId !== "social") return undefined;
    if (socialSummaryCollectionName !== "social_media_report") return undefined;
    if (!resolvedUserId || !authToken) return undefined;

    const shopId = resolvedAssessmentShopId || summaryReportData?.shop_id || null;
    const availableWindows = Array.isArray(summaryReportData?.data?.available_windows)
      ? summaryReportData.data.available_windows
      : [];
    const loadedWindows = Array.isArray(summaryReportData?.data?.windows)
      ? summaryReportData.data.windows
      : [];
    if (!availableWindows.length) return undefined;

    const loadedKeys = new Set(loadedWindows.map((windowEntry) => windowEntry?.key).filter(Boolean));
    const loadKey = `${resolvedUserId}:${shopId || ""}:${socialSummaryGenerationKey}:${availableWindows.map((windowEntry) => windowEntry?.key).join(",")}`;
    if (socialSummaryWindowsLoadKeyRef.current === loadKey) return undefined;

    const remainingWindows = availableWindows
      .filter((windowEntry) => windowEntry?.key && !loadedKeys.has(windowEntry.key))
      .sort((left, right) => Number(left?.days || 0) - Number(right?.days || 0));

    if (!remainingWindows.length) {
      socialSummaryWindowsLoadKeyRef.current = loadKey;
      return undefined;
    }

    let active = true;
    socialSummaryWindowsLoadKeyRef.current = loadKey;

    const hydrateWindows = async () => {
      for (const windowEntry of remainingWindows) {
        if (!active) break;
        try {
          const resp = await fetchSocialMediaAssessmentReport({
            userId: resolvedUserId,
            shopId,
            authToken,
            query: {
              section: "assessment",
              windowKey: windowEntry.key,
              startDate: windowEntry.startDate || null,
              endDate: windowEntry.endDate || null,
            },
          });
          if (!active) break;
          const newWindows = Array.isArray(resp?.report?.data?.windows)
            ? resp.report.data.windows
            : [];
          if (!newWindows.length) continue;

          setSummaryReportData((current) => {
            if (!current?.data) return current;
            const existing = Array.isArray(current.data.windows) ? current.data.windows : [];
            const existingKeys = new Set(existing.map((entry) => entry?.key).filter(Boolean));
            const additions = newWindows.filter((entry) => entry?.key && !existingKeys.has(entry.key));
            if (!additions.length) return current;
            return {
              ...current,
              data: {
                ...current.data,
                available_windows: current.data.available_windows?.length
                  ? current.data.available_windows
                  : resp.report.data.available_windows || [],
                windows: [...existing, ...additions],
              },
            };
          });
        } catch (err) {
          console.warn("[assessment][social] hydrateWindow.error", {
            windowKey: windowEntry?.key || null,
            error: err?.message || String(err),
          });
        }
      }
    };

    const hydrateTimer = window.setTimeout(hydrateWindows, 500);
    return () => {
      active = false;
      window.clearTimeout(hydrateTimer);
    };
  }, [
    isFocusedSummaryView,
    summaryFocusId,
    socialSummaryCollectionName,
    socialSummaryGenerationKey,
    socialSummaryAvailableWindowsKey,
    resolvedAssessmentShopId,
    resolvedUserId,
    authToken,
    summaryReportData?.shop_id,
  ]);

  const conversionSummaryCollectionName = summaryReportData?.data?.collection_name || "";
  const conversionSummaryGenerationKey =
    summaryReportData?.data?.generation_key ||
    summaryReportData?._id ||
    summaryReportData?.id ||
    "";
  const conversionSummaryAvailableWindowsKey = Array.isArray(summaryReportData?.data?.available_windows)
    ? summaryReportData.data.available_windows.map((windowEntry) => windowEntry?.key).join(",")
    : "";

  const expectedSummaryReportCollection = isFocusedConversionView
    ? "conversion_and_pdp_report"
    : isFocusedHomepageView
      ? "homepage_assessment"
      : isFocusedSocialView
        ? "social_media_report"
        : null;
  const focusedSummaryReportIsReady = expectedSummaryReportCollection
    ? summaryReportData?.data?.collection_name === expectedSummaryReportCollection
    : Boolean(summaryReportData);
  const shouldShowFocusedSummarySkeleton =
    summaryLoading &&
    (isFocusedConversionView || isFocusedHomepageView || isFocusedSocialView) &&
    !focusedSummaryReportIsReady;

  useEffect(() => {
    if (!isFocusedSummaryView || summaryFocusId !== "conversion") return undefined;
    if (conversionSummaryCollectionName !== "conversion_and_pdp_report") return undefined;
    if (!resolvedUserId || !authToken) return undefined;

    const shopId = resolvedAssessmentShopId || summaryReportData?.shop_id || null;
    const availableWindows = Array.isArray(summaryReportData?.data?.available_windows)
      ? summaryReportData.data.available_windows
      : [];
    const loadedWindows = Array.isArray(summaryReportData?.data?.windows)
      ? summaryReportData.data.windows
      : [];
    if (!availableWindows.length) return undefined;

    const loadedKeys = new Set(loadedWindows.map((windowEntry) => windowEntry?.key).filter(Boolean));
    const loadKey = `${resolvedUserId}:${shopId || ""}:${conversionSummaryGenerationKey}:${availableWindows.map((windowEntry) => windowEntry?.key).join(",")}`;
    if (conversionSummaryWindowsLoadKeyRef.current === loadKey) return undefined;

    const remainingWindows = availableWindows
      .filter((windowEntry) => windowEntry?.key && !loadedKeys.has(windowEntry.key))
      .sort((left, right) => Number(left?.days || 0) - Number(right?.days || 0));

    if (!remainingWindows.length) {
      conversionSummaryWindowsLoadKeyRef.current = loadKey;
      return undefined;
    }

    let active = true;
    conversionSummaryWindowsLoadKeyRef.current = loadKey;

    const hydrateWindows = async () => {
      for (const windowEntry of remainingWindows) {
        if (!active) break;
        try {
          const resp = await fetchConversionAndPdpAssessmentReport({
            userId: resolvedUserId,
            shopId,
            authToken,
            windowKey: windowEntry.key,
            startDate: windowEntry.startDate || null,
            endDate: windowEntry.endDate || null,
            generationKey: conversionSummaryGenerationKey || null,
          });
          if (!active) break;
          const newWindows = Array.isArray(resp?.report?.data?.windows)
            ? resp.report.data.windows
            : [];
          if (!newWindows.length) continue;

          setSummaryReportData((current) => {
            if (!current?.data) return current;
            const existing = Array.isArray(current.data.windows) ? current.data.windows : [];
            const existingKeys = new Set(existing.map((entry) => entry?.key).filter(Boolean));
            const additions = newWindows.filter((entry) => entry?.key && !existingKeys.has(entry.key));
            if (!additions.length) return current;
            return {
              ...current,
              data: {
                ...current.data,
                available_windows: current.data.available_windows?.length
                  ? current.data.available_windows
                  : resp.report.data.available_windows || [],
                windows: [...existing, ...additions],
              },
            };
          });
        } catch (err) {
          console.warn("[assessment][conversion] hydrateWindow.error", {
            windowKey: windowEntry?.key || null,
            error: err?.message || String(err),
          });
        }
      }
    };

    const hydrateTimer = window.setTimeout(hydrateWindows, 500);
    return () => {
      active = false;
      window.clearTimeout(hydrateTimer);
    };
  }, [
    isFocusedSummaryView,
    summaryFocusId,
    conversionSummaryCollectionName,
    conversionSummaryGenerationKey,
    conversionSummaryAvailableWindowsKey,
    resolvedAssessmentShopId,
    resolvedUserId,
    authToken,
    summaryReportData?.shop_id,
  ]);

  if (error) {
    return (
      <div className={styles.error}>
        <p>{t("StrategyReportSection.loadError")}</p>
        <button type="button" onClick={() => refetch?.()}>
          {t("StrategyReportSection.retry")}
        </button>
      </div>
    );
  }

  if (loading && !data) {
    if (isFocusedConversionView) return <AssessmentReportSkeleton />;
    return (
      <div className={styles.emptyState}>
        <div className={styles.loadingOnly} aria-label="Loading" role="status">
          <span className={styles.loadingSpinner} />
          <p className={styles.loadingText}>
            {t("common.loading") || "Loading"}
          </p>
        </div>
      </div>
    );
  }

  if (!primary) {
    if (isFocusedConversionView) return <AssessmentReportSkeleton />;
    return (
      <div className={styles.emptyState}>
        <div className={styles.loadingOnly} aria-label="Loading" role="status">
          <span className={styles.loadingSpinner} />
          <p className={styles.loadingText}>
            {t("common.loading") || "Loading"}
          </p>
        </div>
      </div>
    );
  }

  if (isOnboardingAssessment) {
    return (
      <div className={`${styles.assessmentLayout} ${styles.assessmentLayoutFull}`}>
        <div className={styles.assessmentColumn}>
          <div className={styles.container}>
            <div className={styles.connectCard}>
              <h4 className={`${styles.connectTitle} ${isArabic ? styles.rtl : ""}`}>
                {t("StrategyReportSection.connect.title")}
              </h4>
              <p className={`${styles.connectBody} ${isArabic ? styles.rtl : ""}`}>
                {t("StrategyReportSection.connect.why")}
              </p>
              <p
                className={`${styles.connectBody} ${isArabic ? styles.rtl : ""}`}
                style={{ marginTop: "0.5rem" }}
              >
                {t("StrategyReportSection.connect.body")}
              </p>
              {(waitingOnGoogleSync || waitingOnFacebookSync) && (
                <p
                  className={`${styles.connectBody} ${isArabic ? styles.rtl : ""}`}
                  style={{ marginTop: "0.75rem" }}
                >
                  {t("StrategyReportSection.connect.syncing")}
                </p>
              )}
              {(tokensLoading ||
                shopDataReadinessLoading ||
                waitingOnGoogleSync ||
                waitingOnFacebookSync) && (
                <div className={styles.statusRow}>
                  <div className={styles.spinner} />
                  <span>{t("StrategyReportSection.connect.checking")}</span>
                </div>
              )}
              <div className={styles.connectButtonsRow}>
                <button
                  type="button"
                  className={`${styles.connectBtn} ${styles.meta}`}
                  onClick={startFacebookOAuth}
                >
                  <span
                    className={`${styles.icon} ${styles.metaIcon}`}
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 48 48" focusable="false">
                      <circle cx="24" cy="24" r="24" fill="#1877F2" />
                      <path
                        fill="#FFFFFF"
                        d="M27.4 38V24.7h4.5l.7-5.2h-5.2v-3.3c0-1.5.4-2.6 2.6-2.6h2.8V9c-.5-.1-2.2-.2-4.2-.2-4.2 0-7 2.6-7 7.3v3.4h-4.7v5.2h4.7V38h4.8z"
                      />
                    </svg>
                  </span>
                  {localFacebookConnected
                    ? t("StrategyReportSection.connect.metaConnected")
                    : t("StrategyReportSection.connect.metaCta")}
                </button>
                <button
                  type="button"
                  className={`${styles.connectBtn} ${styles.google}`}
                  onClick={startGoogleOAuth}
                >
                  <span
                    className={`${styles.icon} ${styles.googleIcon}`}
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 48 48" focusable="false">
                      <path
                        fill="#EA4335"
                        d="M24 9.5c3.54 0 6.72 1.22 9.22 3.61l6.9-6.9C35.9 2.38 30.47 0 24 0 14.62 0 6.44 5.38 2.56 13.22l8.06 6.26C12.5 13.5 17.74 9.5 24 9.5z"
                      />
                      <path
                        fill="#4285F4"
                        d="M46.98 24.55c0-1.64-.15-3.22-.43-4.74H24v9.02h12.94c-.56 3.01-2.24 5.56-4.78 7.27l7.73 5.98c4.51-4.17 7.09-10.32 7.09-17.53z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M10.62 28.48c-.53-1.58-.83-3.27-.83-5s.3-3.42.83-5l-8.06-6.26C.92 15.46 0 19.64 0 24s.92 8.54 2.56 12.78l8.06-6.26z"
                      />
                      <path
                        fill="#34A853"
                        d="M24 48c6.48 0 11.93-2.13 15.9-5.81l-7.73-5.98c-2.15 1.45-4.92 2.3-8.17 2.3-6.26 0-11.5-4-13.38-9.5l-8.06 6.26C6.44 42.62 14.62 48 24 48z"
                      />
                    </svg>
                  </span>
                  {localGoogleConnected
                    ? t("StrategyReportSection.connect.googleConnected")
                    : t("StrategyReportSection.connect.googleCta")}
                </button>
              </div>
              <div className={styles.connectButtonsRow}>
                <button
                  type="button"
                  className={`${styles.connectBtn} ${styles.costBtn} ${
                    productCostReady ? styles.costBtnComplete : ""
                  }`}
                  onClick={() => onOpenProductCosts?.()}
                >
                  {productCostReady
                    ? t("StrategyReportSection.connect.productCostConnected")
                    : t("StrategyReportSection.connect.productCostCta")}
                </button>
                <button
                  type="button"
                  className={`${styles.connectBtn} ${styles.costBtn} ${
                    operatingCostReady ? styles.costBtnComplete : ""
                  }`}
                  onClick={() => onOpenOperatingCosts?.()}
                >
                  {operatingCostReady
                    ? t("StrategyReportSection.connect.operatingCostConnected")
                    : t("StrategyReportSection.connect.operatingCostCta")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === "strategy") {
    return (
      <div
        className={`${styles.assessmentLayout} ${styles.assessmentLayoutFull}`}
      >
        <div className={styles.assessmentColumn}>
          <div className={styles.container}>
            {(() => {
              if (!showStrategySwitch) return null;
              const switchNode = (
                <div className={styles.assessmentTabs} role="tablist">
                  <button
                    type="button"
                    className={`${styles.assessmentTab} ${
                      activeStrategyTab === "foundation"
                        ? styles.assessmentTabActive
                        : ""
                    }`}
                    onClick={() => setActiveStrategyTab("foundation")}
                    role="tab"
                    aria-selected={activeStrategyTab === "foundation"}
                  >
                    {t("StrategyReportSection.assessmentTabFoundation") ||
                      "Foundational"}
                  </button>
                  <button
                    type="button"
                    className={`${styles.assessmentTab} ${
                      activeStrategyTab === "performance"
                        ? styles.assessmentTabActive
                        : ""
                    }`}
                    onClick={() => setActiveStrategyTab("performance")}
                    role="tab"
                    aria-selected={activeStrategyTab === "performance"}
                  >
                    {t("StrategyReportSection.assessmentTabPerformance") ||
                      "Business Reality Analysis"}
                  </button>
                </div>
              );
              if (assessmentSwitchTargetId && isDesktop) {
                const target = document.getElementById(
                  assessmentSwitchTargetId,
                );
                if (target) return createPortal(switchNode, target);
              }
              return switchNode;
            })()}
            <article
              key={activeStrategyTab}
              className={`${styles.strategyCard} ${styles.assessmentTransition}`}
            >
              {/* <div className={styles.cardHeader}>
                <h3 className={`${styles.cardTitle} ${isArabic ? styles.rtl : ""}`}>
                  {activeStrategyStrategy?.title ||
                    t("StrategyReportSection.strategyTitle") ||
                    "Strategy"}
                </h3>
              </div> */}
              <p
                className={`${styles.description} ${isArabic ? styles.rtl : ""}`}
              >
                {stripMd(activeStrategyData.strategyDesc)}
              </p>
              {Array.isArray(activeStrategyData.strategyArr) &&
                activeStrategyData.strategyArr.length > 0 && (
                  <div className={styles.strategyStack}>
                    {activeStrategyData.strategyArr
                      .filter(Boolean)
                      .map((item, idx) => {
                        const label = getAssessmentLabel(item, idx);
                        return { item, idx, label };
                      })
                      .sort((a, b) => {
                        const ai = isActiveStrategyOnboarding
                          ? getOnboardingFlowIndex(a.label)
                          : getStrategyFlowIndex(a.label);
                        const bi = isActiveStrategyOnboarding
                          ? getOnboardingFlowIndex(b.label)
                          : getStrategyFlowIndex(b.label);
                        if (ai !== bi) return ai - bi;
                        return a.idx - b.idx;
                      })
                      .map((item) => {
                        const label = item.label;
                        return (
                          <div
                            key={`${label}-${item.idx}`}
                            className={styles.strategyStackCard}
                          >
                            {isActiveStrategyOnboarding
                              ? renderOnboardingStrategySection(
                                  label,
                                  item.item,
                                )
                              : isActiveStrategyFoundational
                                ? renderFoundationalStrategySection(
                                    label,
                                    item.item,
                                  )
                                : renderFoundationalStrategySection(
                                    label,
                                    item.item,
                                  )}
                          </div>
                        );
                      })}
                  </div>
                )}
            </article>
          </div>
        </div>
        {/* <div className={styles.researchColumn}>
          <ZeqiProblemAndResultSection research={activeStrategyStrategy?.research} />
        </div> */}
        <StrategyFullReportModal
          isOpen={Boolean(fullReportModal)}
          title={fullReportModal?.title || ""}
          subtitle={fullReportModal?.subtitle || ""}
          clusterFlag={fullReportModal?.clusterFlag || ""}
          content={fullReportModal?.content || ""}
          onClose={closeFullReport}
          initialSectionIndex={fullReportModal?.initialSectionIndex ?? 0}
        />
      </div>
    );
  }

  return (
    <div
      className={`${styles.assessmentLayout} ${
        assessmentReport ? "" : styles.assessmentLayoutSingle
      } ${view === "assessment" ? styles.assessmentLayoutFull : ""}`}
    >
      {assessmentReport && (
        <div className={styles.assessmentColumn}>
          <div className={styles.container}>
            {(() => {
              if (!showAssessmentSwitch || isFocusedSummaryView) return null;
              const switchNode = (
                <div className={styles.assessmentTabs} role="tablist">
                  <button
                    type="button"
                    className={`${styles.assessmentTab} ${
                      activeAssessmentTab === "summary"
                        ? styles.assessmentTabActive
                        : ""
                    }`}
                    onClick={() => {
                      setActiveAssessmentTab("summary");
                      setInlineAssessmentReport(null);
                      if (!isFreeTierAssessmentView) {
                        loadSummaryReport();
                      }
                    }}
                    role="tab"
                    aria-selected={activeAssessmentTab === "summary"}
                  >
                    {t("StrategyReportSection.assessmentTabSummary") || "Summary"}
                  </button>
                  <button
                    type="button"
                    className={`${styles.assessmentTab} ${
                      activeAssessmentTab === "foundation"
                        ? styles.assessmentTabActive
                        : ""
                    }`}
                    onClick={() => {
                      setActiveAssessmentTab("foundation");
                      setInlineAssessmentReport(null);
                    }}
                    role="tab"
                    aria-selected={activeAssessmentTab === "foundation"}
                  >
                    {t("StrategyReportSection.assessmentTabFoundation") ||
                      "Foundational Assessment"}
                  </button>
                  <button
                    type="button"
                    className={`${styles.assessmentTab} ${
                      activeAssessmentTab === "performance"
                        ? styles.assessmentTabActive
                        : ""
                    }`}
                    onClick={() => {
                      setActiveAssessmentTab("performance");
                      setInlineAssessmentReport(null);
                    }}
                    role="tab"
                    aria-selected={activeAssessmentTab === "performance"}
                  >
                    {t("StrategyReportSection.assessmentTabPerformance") ||
                      "Performance Assessment"}
                  </button>
                </div>
              );
              if (assessmentSwitchTargetId && isDesktop) {
                const target = document.getElementById(
                  assessmentSwitchTargetId,
                );
                if (target) return createPortal(switchNode, target);
              }
              return switchNode;
            })()}
            <article
              key={isFocusedSummaryView ? `summary-${summaryFocusId}` : activeAssessmentTab}
              className={`${styles.card} ${styles.assessmentTransition}`}
            >
              {shouldRenderSummaryAssessment ? (
                <div>
                  {!isFocusedConversionView && !isFocusedSocialView && !isFocusedHomepageView ? (
                    <div className={styles.cardHeader}>
                      <h3
                        className={`${styles.cardTitle} ${isArabic ? styles.rtl : ""}`}
                      >
                        {summaryFocus?.label ||
                          t("StrategyReportSection.assessmentTabSummary") ||
                          "Summary"}
                      </h3>
                    </div>
                  ) : null}

                  {isSummaryLockedForFree ? (
                    <div className={styles.lockedAssessmentCard}>
                      <div className={styles.lockedAssessmentBadge}>
                        Monthly plan
                      </div>
                      <h4 className={styles.lockedAssessmentTitle}>
                        Unlock Summary
                      </h4>
                      <p className={styles.lockedAssessmentBody}>
                        See the synthesized top-line read on your business reality,
                        key growth signals, and the highest-priority areas to act on.
                      </p>
                      <p className={styles.lockedAssessmentBody}>
                        This assessment is available on the Monthly Subscription
                        plan. Upgrade to continue.
                      </p>
                      <button
                        type="button"
                        className={styles.lockedAssessmentButton}
                        onClick={() => navigate("/billing")}
                      >
                        Upgrade in Billing
                      </button>
                    </div>
                  ) : shouldShowFocusedSummarySkeleton || (summaryLoading && !summaryReportData) ? (
                    <AssessmentReportSkeleton />
                  ) : summaryError ? (
                    <p className={styles.description}>{summaryError}</p>
                  ) : (
                    <div>
                      {isFocusedConversionView ? (
                        (() => {
                          console.log("[assessment][conversion] render-branch", {
                            summaryFocusId,
                            reportId:
                              summaryReportData?._id || summaryReportData?.id || null,
                            collectionName:
                              summaryReportData?.data?.collection_name || null,
                            generationKey:
                              summaryReportData?.data?.generation_key || null,
                            reportingPeriod:
                              summaryReportData?.data?.reporting_period || null,
                          });
                          return summaryReportData?.data?.collection_name ===
                            "conversion_and_pdp_report" ? (
                          <div className={styles.focusedSummaryCard}>
                            <ConversionAndPdpViewer
                              data={summaryReportData.data}
                              report={summaryReportData}
                            />
                          </div>
                        ) : (
                          <p className={styles.description}>
                            Conversion report not available yet.
                          </p>
                        );
                        })()
                      ) : isFocusedSummaryView && summaryFocusId === "performance" ? (
                        <MetaPerformanceAssessmentViewer
                          userId={resolvedUserId}
                          authToken={authToken}
                          shopId={resolvedAssessmentShopId}
                        />
                      ) : isFocusedSummaryView && summaryFocusId === "homepage" ? (
                        summaryReportData?.data?.collection_name === "homepage_assessment" ? (
                          <div className={styles.focusedSummaryCard}>
                            <HomepageAssessmentViewer
                              data={summaryReportData.data}
                              report={summaryReportData}
                              onWindowChange={onWindowChange}
                            />
                          </div>
                        ) : (
                          <p className={styles.description}>
                            Homepage assessment not available yet.
                          </p>
                        )
                      ) : isFocusedSummaryView && summaryFocusId === "social" ? (
                        summaryReportData?.data?.collection_name === "social_media_report" ? (
                          <div className={styles.focusedSummaryCard}>
                            <SocialMediaAssessmentViewer
                              data={summaryReportData.data}
                              report={summaryReportData}
                              onWindowChange={onWindowChange}
                            />
                          </div>
                        ) : (
                          <p className={styles.description}>
                            Social media report not available yet.
                          </p>
                        )
                      ) : isFocusedSummaryView && summaryFocusId !== "business" ? (
                        focusedSummarySections.length ? (
                          <div className={styles.assessmentStack}>
                            {focusedSummarySections.map((section) => (
                              <div
                                key={section.key}
                                className={styles.focusedSummaryCard}
                              >
                                {(() => {
                                  const meta =
                                    FOCUSED_SUMMARY_SECTION_META[section.key] || {};
                                  const IconComponent = meta.Icon || BarChart3;
                                  return (
                                    <div className={styles.focusedSummaryHero}>
                                      <div className={styles.focusedSummaryHeroLeft}>
                                        <div
                                          className={styles.focusedSummaryHeroIcon}
                                          aria-hidden="true"
                                        >
                                          <IconComponent
                                            className={styles.focusedSummaryHeroGlyph}
                                          />
                                        </div>
                                        <h4 className={styles.focusedSummaryHeroTitle}>
                                          {section.title}
                                        </h4>
                                      </div>
                                      <p className={styles.focusedSummaryHeroSubtitle}>
                                        {meta.subtitle || "Assessment insight"}
                                      </p>
                                    </div>
                                  );
                                })()}
                                {section.key === "revenue_reality" &&
                                section.assessmentReportBody ? (
                                  <>
                                    {section.overviewBody ? (
                                      <div
                                        className={`${styles.focusedSummaryBody} ${styles.summaryMarkdown} ${isArabic ? styles.rtl : ""}`}
                                      >
                                        <ReactMarkdown>{section.overviewBody}</ReactMarkdown>
                                      </div>
                                    ) : null}
                                    <div className={styles.focusedSummarySectionHeader}>
                                      <span className={styles.focusedSummarySectionTitle}>
                                        Business Reality Analysis
                                      </span>
                                    </div>
                                    <div
                                      className={`${styles.focusedSummaryBody} ${styles.summaryMarkdown} ${isArabic ? styles.rtl : ""}`}
                                    >
                                      <ReactMarkdown>{section.assessmentReportBody}</ReactMarkdown>
                                    </div>
                                  </>
                                ) : section.assessmentReportItems?.length ? (
                                  <>
                                    {section.overviewBody ? (
                                      <div
                                        className={`${styles.focusedSummaryBody} ${styles.summaryMarkdown} ${isArabic ? styles.rtl : ""}`}
                                      >
                                        <ReactMarkdown>{section.overviewBody}</ReactMarkdown>
                                      </div>
                                    ) : null}
                                    <div className={styles.assessmentStack}>
                                      {section.assessmentReportItems.map((item, idx) => (
                                        <div
                                          key={`${section.key}-${idx}`}
                                          className={styles.assessmentStackCard}
                                        >
                                          {isNarrativeAssessmentLine(item) ? (
                                            <FoundationNarrativeAssessmentLineRenderer
                                              markdown={item}
                                            />
                                          ) : (
                                            <div
                                              className={`${styles.focusedSummaryBody} ${styles.summaryMarkdown} ${isArabic ? styles.rtl : ""}`}
                                            >
                                              <ReactMarkdown>{item}</ReactMarkdown>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </>
                                ) : (
                                  <div
                                    className={`${styles.focusedSummaryBody} ${styles.summaryMarkdown} ${isArabic ? styles.rtl : ""}`}
                                  >
                                    <ReactMarkdown>{section.body}</ReactMarkdown>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : summaryMarkdown ? (
                          <div
                            className={`${styles.summaryMarkdown} ${isArabic ? styles.rtl : ""}`}
                          >
                            <ReactMarkdown>{summaryMarkdown}</ReactMarkdown>
                          </div>
                        ) : (
                          <p className={styles.description}>
                            {t("StrategyReportSection.noExtra") ||
                              "No additional report content yet."}
                          </p>
                        )
                      ) : summaryMarkdown ? (
                        <div
                          className={`${styles.summaryMarkdown} ${isArabic ? styles.rtl : ""}`}
                        >
                          <ReactMarkdown>{summaryMarkdown}</ReactMarkdown>
                        </div>
                      ) : (
                        <p className={styles.description}>
                          {t("StrategyReportSection.noExtra") ||
                            "No additional report content yet."}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>
              {!inlineAssessmentReport ? (
                <>
                  <div className={styles.cardHeader}>
                    <h3
                      className={`${styles.cardTitle} ${isArabic ? styles.rtl : ""}`}
                    >
                      {assessmentReport.title}
                    </h3>
                    {/* <button
                      type="button"
                      className={styles.pillButton}
                      onClick={() =>
                        openFullReport(
                          assessmentReport,
                          activeAssessmentStrategy,
                        )
                      }
                    >
                      {t("StrategyReportSection.assessmentButton")}
                    </button> */}
                  </div>

                  <p
                    className={`${styles.description} ${isArabic ? styles.rtl : ""}`}
                  >
                    {stripMd(assessmentReport.description)}
                  </p>
                </>
              ) : null}

              {/* <button
                type="button"
                className={styles.discussButton}
                onClick={() => handleDiscuss(assessmentReport)}
              >
                {t("StrategyReportSection.discuss")}
              </button> */}

              {assessmentItems.length > 0 &&
                (inlineAssessmentReport ? (
                  <div className={styles.inlineAssessmentViewer}>
                    <div className={styles.inlineAssessmentHeader}>
                      <button
                        type="button"
                        className={styles.inlineCloseButton}
                        onClick={() => setInlineAssessmentReport(null)}
                        aria-label="Close"
                      >
                        <ChevronLeft size={16} />
                      </button>
                    </div>
                    <StrategyFullReportModal
                      isOpen={Boolean(inlineAssessmentReport)}
                      clusterFlag={inlineAssessmentReport.clusterFlag}
                      content={inlineAssessmentReport.content}
                      onClose={() => setInlineAssessmentReport(null)}
                      variant="page"
                      showCloseButton={false}
                      initialSectionIndex={
                        inlineAssessmentReport.initialSectionIndex ?? 0
                      }
                    />
                  </div>
                ) : (
                  <div className={styles.assessmentStack}>
                    {assessmentItems.map((item, idx) => {
                      const label = getAssessmentLabel(item, idx);
                      const IconComponent = getSectionIcon(label);
                      const isLockedForFree =
                        isFreeTierAssessmentView &&
                        !isFreeAccessibleAssessment({
                          label,
                          isFunnel: isActiveAssessmentFunnel,
                        });
                      const lockedAssessmentTitle =
                        getLockedAssessmentTitle(label);
                      const lockedAssessmentTeaser =
                        getLockedAssessmentTeaser(label);
                      return (
                        <div
                          key={`${label}-${idx}`}
                          className={styles.assessmentStackCard}
                        >
                          {isLockedForFree ? (
                            <div className={styles.lockedAssessmentCard}>
                              <div className={styles.lockedAssessmentBadge}>
                                Monthly plan
                              </div>
                              <h4 className={styles.lockedAssessmentTitle}>
                                {lockedAssessmentTitle}
                              </h4>
                              <p className={styles.lockedAssessmentBody}>
                                {lockedAssessmentTeaser}
                              </p>
                              <button
                                type="button"
                                className={styles.lockedAssessmentButton}
                                onClick={() => navigate("/billing")}
                              >
                                Upgrade in Billing
                              </button>
                            </div>
                          ) : isActiveAssessmentFunnel ? (
                            <div className={styles.assessmentStackViewerWrap}>
                              {renderInlineFunnelAssessmentViewer({
                                label,
                                markdown: item,
                              })}
                            </div>
                          ) : (
                            <>
                              {isNarrativeAssessmentLine(item) ? (
                                <FoundationNarrativeAssessmentLineRenderer
                                  markdown={item}
                                />
                              ) : (
                                <>
                                  <div className={styles.assessmentStackButtonHeader}>
                                    <span className={styles.assessmentStackIconWrap}>
                                      <IconComponent
                                        size={16}
                                        className={styles.assessmentGridIcon}
                                      />
                                    </span>
                                    <span className={styles.assessmentStackTitle}>
                                      {label}
                                    </span>
                                  </div>
                                  <p className={styles.assessmentStackBodyFull}>
                                    {stripMd(item) || getAssessmentPreviewText(item)}
                                  </p>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
                </>
              )}
            </article>
          </div>
        </div>
      )}
      <StrategyFullReportModal
        isOpen={Boolean(fullReportModal)}
        title={fullReportModal?.title || ""}
        subtitle={fullReportModal?.subtitle || ""}
        clusterFlag={fullReportModal?.clusterFlag || ""}
        content={fullReportModal?.content || ""}
        onClose={closeFullReport}
        initialSectionIndex={fullReportModal?.initialSectionIndex ?? 0}
      />
    </div>
  );
};

export default ZeqiStrategyReportSection;
